/* eslint-disable */
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ContentArea,
  ContentContainer,
  DialogBody,
  TabsContainer,
  TabsList,
  TabItem,
  TabIcon,
  TabIndicator,
  SlidingPanelOverlay,
  SlidingPanelContainer,
  SlidingPanelHeader,
  SlidingPanelTitle,
  SlidingPanelCloseButton,
  SlidingPanelBody,
  FullQuestionnaireReadonly,
} from './index';
import type { SubmitTabRef } from './tabs';

import { tabItems } from './constants';
import {
  useHotelQuestionnaire,
  useHotelQuestionnaireSteps,
  useHotelSources,
  useToast,
  useReadonlyQuestionnaire,
  apiUtils,
} from '../../../hooks';
import { useHotelQuestionnaireContext } from '../../../contexts/HotelQuestionnaireContext';
import { Button } from '../Button';
import { QuestionnaireType, Hotel } from '../../../hooks/useHotels';
import { ArrowRightLeft, FileIcon, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../../services/apiEndpoints';
import Loader from '../../Loader';
import { findFirstMissingAttachmentQuestion } from '../../../utils/attachmentValidationUtils';
import { useHotelDetail } from '../../../contexts/HotelDetailContext';
import { superAdminService } from '../../../services/superAdminService';
import { BUTTON_TEXTS } from '../../../constants';
import { NavigationTab } from '../../../pages/HotelDetail';
import { Tooltip } from '../Tooltip';
import { AutoPublishTimer } from '../AutoPublishTimer';
import { ConfirmationModal } from '../ConfirmationModal';
import { NavigationConfirmModal } from '../NavigationConfirmModal';
import SwitchAssessmentPathModal from './SwitchAssessmentPathModal';
import Benchmark from './tabs/Benchmark';
import submissionsService from '../../../services/submissionsService';
const SubmitTab = lazy(() =>
  import('./tabs').then(module => ({ default: module.SubmitTab }))
);
const ScoreTab = lazy(() =>
  import('./tabs').then(module => ({ default: module.ScoreTab }))
);
const TravelTab = lazy(() =>
  import('./tabs').then(module => ({ default: module.TravelTab }))
);
const HotelLinksTab = lazy(() => import('./tabs/HotelLinksTab'));

interface HotelDetailDialogBodyProps {
  questionnaireType: QuestionnaireType;
  activeTab: NavigationTab;
  setActiveTab: (tab: NavigationTab) => void;
  hotel: Hotel;
}
const HotelDetailDialogBody = ({
  questionnaireType,
  activeTab,
  setActiveTab,
  hotel,
}: HotelDetailDialogBodyProps) => {
  console.log('Rendering HotelDetailDialogBody with hotel:', hotel);
  const { user, isSuperAdmin, isHotelAdmin } = useAuth();
  const {
    updateHotelStatus,
    refetchHotel,
    refetchHotelSilently,
    hotel: liveHotel,
  } = useHotelDetail();
  const submitTabRef = useRef<SubmitTabRef>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  // const [activeTab, setActiveTab] = useState('submit');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [approvedSourcesCount, setApprovedSourcesCount] = useState(0);
  const [isDetailedAssessmentOpen, setIsDetailedAssessmentOpen] =
    useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [isSettingAsScored, setIsSettingAsScored] = useState(false);
  const [isCalculatingScore, setIsCalculatingScore] = useState(false);
  const [isApprovingScore, setIsApprovingScore] = useState(false);
  const [isPublishingScore, setIsPublishingScore] = useState(false);
  const [isReopeningSubmission, setIsReopeningSubmission] = useState(false);
  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  const [showReopenConfirmModal, setShowReopenConfirmModal] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [showHotelAdminSwitchModal, setShowHotelAdminSwitchModal] =
    useState(false);
  const [isSwitchingQuestionnaire, setIsSwitchingQuestionnaire] =
    useState(false);
  const [isSavingAndSwitching, setIsSavingAndSwitching] = useState(false);
  const navigate = useNavigate();
  const { currentStepKey, isLastStep, stepsData } =
    useHotelQuestionnaireSteps();
  // Silently refresh hotel permissions when user reaches sources (last step)
  // so canSubmitQuestionnaire is up-to-date. Uses silent refetch to avoid
  // triggering hotelLoading which would unmount the page and cause a loop.
  useEffect(() => {
    if (currentStepKey === 'sources') {
      refetchHotelSilently();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepKey]);
  const { submitQuestionnaire, questionnaireLoading, questionnaireError } =
    useHotelQuestionnaire();
  const { state, actions: contextActions } = useHotelQuestionnaireContext();
  const { fetchReadonlyQuestionnaire } = useReadonlyQuestionnaire();
  const { success, error } = useToast();

  // Compute active questionnaire type: prefer context state (updated on switch) over prop
  const activeQuestionnaireType = state.questionnaireType || questionnaireType;

  // Initialize context questionnaireType on mount / hotel change.
  // Super admins always default to 'FULL'; regular users use the hotel's questionnaire_type.
  // Only set if null (not yet initialized) so a user-initiated switch is not overwritten on remount.
  useEffect(() => {
    if (state.questionnaireType === null) {
      const initialType: 'FASTTRACK' | 'FULL' = isSuperAdmin
        ? 'FULL'
        : (hotel.questionnaire_type as 'FASTTRACK' | 'FULL') || 'FULL';
      contextActions.setQuestionnaireType(initialType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel.id]);
  const {
    sourcesData,
    sourcesLoading,
    sourcesError,
    fetchSources,
    saveSourcesReview,
    approvedCount: approvedSourcesCount,
  } = useHotelSources();

  // Calculate approval status for super admin info banner
  const getApprovalStatus = () => {
    if (!sourcesData?.summary) return null;

    const totalAttachments = sourcesData.summary.total_attachments || 0;
    const approvedCount = approvedSourcesCount || 0;

    if (totalAttachments === 0) return null;

    if (totalAttachments <= 3) {
      const isComplete = approvedCount === totalAttachments;
      const requiredCount = totalAttachments;
      return { isComplete, approvedCount, requiredCount, totalAttachments };
    } else {
      const isComplete = approvedCount >= 3;
      const requiredCount = 3;
      return { isComplete, approvedCount, requiredCount, totalAttachments };
    }
  };

  const approvalStatus = getApprovalStatus();

  // Get permissions from live hotel in context (keeps up-to-date after step navigation / refetch)
  const permissions = (liveHotel ?? hotel).userPermissions || {
    canSaveQuestionnaire: false,
    canSubmitQuestionnaire: false,
    canResubmitQuestionnaire: false,
    canApproveScore: false,
    canPublishScore: false,
    canSaveReview: false,
    canCalculateAloScore: false,
    canReleaseToHotel: false,
    canViewBenchmark: false,
    canReopenSubmission: false,
  };

  // Note: fetchSources is handled by SubmitTab when needed
  // Removed redundant useEffect to prevent duplicate API calls

  // Update indicator position when active tab changes
  useEffect(() => {
    const activeTabElement = tabRefs.current[activeTab];
    if (activeTabElement) {
      const { offsetLeft, offsetWidth } = activeTabElement;
      const offsetAdjustment = 12; // Adjust for padding/margin if needed
      setIndicatorStyle({
        left: offsetLeft,
        width: offsetWidth + offsetAdjustment,
      });
    }
  }, [activeTab]);

  const handleTabClick = (tabId: NavigationTab, disabled: boolean) => {
    if (!disabled) {
      setActiveTab(tabId);
    }
  };

  const handleDetailedAssessmentClick = () => {
    setIsDetailedAssessmentOpen(true);
    setIsClosing(false);
    setIsOpening(true);
    // Fetch readonly questionnaire data when opening detailed assessment
    fetchReadonlyQuestionnaire();
    // Trigger the slide-in animation after a small delay
    setTimeout(() => {
      setIsOpening(false);
    }, 10);
  };

  const handleCloseDetailedAssessment = () => {
    setIsClosing(true);
    setIsOpening(false);
    // Wait for animation to complete before hiding
    setIsDetailedAssessmentOpen(false);
    setIsClosing(false);
  };

  const saveReview = async (showToast: boolean = true) => {
    console.log('Saving sources review');

    const response = await saveSourcesReview(showToast);

    if (!response) {
      return;
    }
    // Check if response indicates missing attachments
    if (
      response &&
      typeof response === 'object' &&
      'hasMissingAttachments' in response
    ) {
      if ((response as any).hasMissingAttachments) {
        showToast &&
          success('Sources review saved successfully', { duration: 3000 });
        setTimeout(() => {
          error(
            'You need to upload attachments for the questions to submit the questionnaire',
            {
              duration: 5000,
            }
          );
        }, 500);
      } else {
        showToast && success('Sources review saved successfully');
      }
    } else {
      showToast && success('Sources review saved successfully');
    }
  };

  const handleSubmitQuestionnaire = async () => {
    setIsSubmitting(true);
    try {
      // If ALO_SUPER is in sources section, validate and save
      // if (isSuperAdmin && currentStepKey === 'sources') {
      //   await saveReview();
      // } else {

      // }
      // For REGULAR USERS: Check ENTIRE QUESTIONNAIRE for missing attachments before allowing submission
      // If we're in sources step, use sourcesData; otherwise use questionnaireData
      const dataToValidate =
        currentStepKey === 'sources'
          ? state.sourcesData
          : state.questionnaireData;

      if (dataToValidate && state.answers) {
        console.log('Validating questionnaire for missing attachments:', {
          currentStepKey,
          dataToValidate,
          answers: state.answers,
        });

        const firstMissingQuestion = findFirstMissingAttachmentQuestion(
          dataToValidate as any,
          state.answers
        );

        if (firstMissingQuestion) {
          console.log('Found missing attachment:', firstMissingQuestion);
          error(
            `Please upload attachment for: ${firstMissingQuestion.questionLabel.slice(
              0,
              50
            )}${firstMissingQuestion.questionLabel.length > 50 ? '...' : ''}`,
            { duration: 5000 }
          );

          // If we're in sources step and have a groupKey, scroll to the question
          if (
            currentStepKey === 'sources' &&
            submitTabRef.current &&
            firstMissingQuestion.groupKey
          ) {
            console.log('Scrolling to question in sources tab:', {
              questionId: firstMissingQuestion.questionId,
              groupKey: firstMissingQuestion.groupKey,
            });
            submitTabRef.current.scrollToQuestion(
              firstMissingQuestion.questionId,
              firstMissingQuestion.groupKey
            );
          }

          setIsSubmitting(false);
          return; // Prevent submission
        }
      }

      await submitQuestionnaire();
      success('Successfully submitted the questionnaire');
      navigate(isSuperAdmin ? '/hotels' : '/');
    } catch (err) {
      // Error: show toast with error message and stay on page
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      error(`${errorMessage}`);
      console.error('Failed to submit questionnaire:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetAsScored = async () => {
    setIsSettingAsScored(true);
    try {
      console.log('Setting as scored');
      const response = await superAdminService.releaseToHotel(
        hotel.id,
        state.questionnaireType === 'FASTTRACK'
      );
      console.log('Set as scored response:', response);
      success(
        (response as any)?.data.message ||
          'Hotel score released to hotel successfullyyy',
        { duration: 2000 }
      );
      navigate('/hotels');
    } catch (err: any) {
      console.error('error', err);
      error(`Failed to release score: ${err.message}`, { duration: 5000 });
    } finally {
      setIsSettingAsScored(false);
    }
  };

  const handleCalculateALOScore = async () => {
    setIsCalculatingScore(true);
    try {
      console.log('Calculating ALO Score');
      await saveReview(false);
      //calculate alo score
      await superAdminService.calculateAloScore(
        hotel.id,
        state.questionnaireType === 'FASTTRACK'
      );
      success('ALO Score calculated successfully', { duration: 2000 });
    } catch (err: any) {
      error(`Failed to calculate Alō Score: ${err.message}`, {
        duration: 5000,
      });
    } finally {
      setIsCalculatingScore(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'submit':
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-64">
                <Loader />
              </div>
            }
          >
            <SubmitTab
              ref={submitTabRef}
              // onApprovalCountChange={setApprovedSourcesCount}
              hotel={hotel}
            />
          </Suspense>
        );
      case 'score':
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-64">
                <Loader />
              </div>
            }
          >
            <ScoreTab hotel={hotel} />
          </Suspense>
        );
      case 'travel':
        return hotel ? (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-64">
                <Loader />
              </div>
            }
          >
            <TravelTab hotel={hotel} />
          </Suspense>
        ) : null;
      case 'hotel-orgs-enterprises':
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-64">
                <Loader />
              </div>
            }
          >
            <HotelLinksTab />
          </Suspense>
        );
      case 'benchmark':
        return (
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-64">
                <Loader />
              </div>
            }
          >
            <Benchmark />
          </Suspense>
        );
      default:
        return null;
    }
  };

  const renderApprovalStatusBanner = () => {
    if (!isSuperAdmin || currentStepKey !== 'sources') {
      return null;
    }

    // Check if there are no attachments
    const totalAttachments = sourcesData?.summary?.total_attachments || 0;
    if (totalAttachments === 0) {
      return (
        <div className="mb-4 p-4 rounded-lg border-l-4 border bg-green-50 border-planet-green flex-1">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0 text-planet-green">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-sm mb-1 text-planet-green">
                No Attachments Found
              </h4>
              <p className="text-sm text-planet-green">
                No attachments to approve. You can directly generate score.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (!approvalStatus) {
      return null;
    }

    return (
      <div
        className={`mb-4 p-4 rounded-lg border-l-4 ${
          approvalStatus.isComplete
            ? 'bg-green-50 border-green-500'
            : 'bg-amber-50 border-amber-500'
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 flex-shrink-0 ${
              approvalStatus.isComplete ? 'text-green-600' : 'text-amber-600'
            }`}
          >
            {approvalStatus.isComplete ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <h4
              className={`font-semibold text-sm mb-1 ${
                approvalStatus.isComplete ? 'text-green-800' : 'text-amber-800'
              }`}
            >
              {approvalStatus.isComplete
                ? 'Approval Requirements Met'
                : 'Approval Requirements Not Met'}
            </h4>
            <p
              className={`text-sm ${
                approvalStatus.isComplete ? 'text-green-700' : 'text-amber-700'
              }`}
            >
              {approvalStatus.totalAttachments <= 3 ? (
                <>
                  <span className="font-medium">
                    {approvalStatus.approvedCount}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">
                    {approvalStatus.totalAttachments}
                  </span>{' '}
                  attachment{approvalStatus.totalAttachments !== 1 ? 's' : ''}{' '}
                  approved.{' '}
                  {!approvalStatus.isComplete && (
                    <span className="font-medium">
                      Please approve all {approvalStatus.totalAttachments}{' '}
                      attachment
                      {approvalStatus.totalAttachments !== 1 ? 's' : ''} before
                      proceeding. Please approve all{' '}
                      {approvalStatus.totalAttachments} attachment
                      {approvalStatus.totalAttachments !== 1 ? 's' : ''} before
                      proceeding.
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="font-medium">
                    {approvalStatus.approvedCount}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">
                    {approvalStatus.totalAttachments}
                  </span>{' '}
                  attachments approved.{' '}
                  {!approvalStatus.isComplete && (
                    <span className="font-medium">
                      At least <span className="font-semibold">3</span>{' '}
                      attachments must be approved before proceeding.
                    </span>
                  )}
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const checkLoading = () => {
    if (currentStepKey !== 'sources') {
      return !questionnaireLoading && !questionnaireError;
    }
    return !sourcesLoading && !sourcesError && sourcesData?.groups;
  };

  const handleApproveScore = async () => {
    setIsApprovingScore(true);
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.aloAdmin.approveScore(hotel.id)
      );
      success((response as any).data.message || 'Score approved successfully', {
        duration: 2000,
      });
      refetchHotel();
      // Update local hotel state
      updateHotelStatus('APPROVED SCORE');
    } catch (err) {
      console.error('Failed to approve score:', err);
      const errorMessage =
        err instanceof Error
          ? (err as any).response?.data?.message
          : 'Unknown error occurred';
      error(`Failed to approve score: ${errorMessage}`);
    } finally {
      setIsApprovingScore(false);
    }
  };

  const handlePublishScore = async () => {
    setIsPublishingScore(true);
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.aloAdmin.publishScore(hotel.id)
      );
      success(
        (response as any).data.message ||
          'Score Published to Hotel Successfully',
        { duration: 2000 }
      );
      // Update local hotel state
      // updateHotelStatus('PUBLISHED');
      navigate('/');
    } catch (err) {
      console.error('Failed to publish score:', err);
      const errorMessage =
        err instanceof Error
          ? (err as any).response?.data?.message
          : 'Unknown error occurred';
      error(`Failed to publish score: ${errorMessage}`);
    } finally {
      setIsPublishingScore(false);
    }
  };

  const handleReopenSubmission = async () => {
    try {
      setIsReopeningSubmission(true);
      await submissionsService.reOpenSubmission(hotel.id);
      refetchHotel();
      setShowReopenConfirmModal(false);
    } catch (err: any) {
      error(err.message || 'Failed to reopen submission');
    } finally {
      setIsReopeningSubmission(false);
    }
  };

  const handleSwitchQuestionnaire = async () => {
    if (contextActions.hasDraftChanges()) {
      setShowSwitchModal(true);
    } else {
      await performSwitch();
    }
  };

  const performSwitch = async () => {
    const newType: 'FASTTRACK' | 'FULL' =
      activeQuestionnaireType === 'FASTTRACK' ? 'FULL' : 'FASTTRACK';
    const isFasttrackForNewType = newType === 'FASTTRACK';
    contextActions.setSwitchingType(true);
    try {
      contextActions.setQuestionnaireType(newType);
      await contextActions.refreshSteps(
        undefined,
        false,
        isFasttrackForNewType
      );
      await contextActions.fetchSubmissions(isFasttrackForNewType);
    } finally {
      contextActions.setSwitchingType(false);
      setShowSwitchModal(false);
    }
  };

  const handleSwitchDiscardAndContinue = async () => {
    setIsSwitchingQuestionnaire(true);
    try {
      // Clear attachments for draft answers before discarding (mirrors confirmNavigation logic)
      if (
        state.hotelId &&
        state.questionnaireData &&
        contextActions.hasDraftChanges()
      ) {
        try {
          const { clearAttachmentsForDraftAnswers } =
            await import('../../../utils/attachmentDeletionUtils');
          const { isStandardQuestionnaire, isGroupedQuestionnaire } =
            await import('../../../utils/questionnaireUtils');

          let questions: any[] = [];
          if (isStandardQuestionnaire(state.questionnaireData)) {
            questions = state.questionnaireData.metadata.fields;
          } else if (isGroupedQuestionnaire(state.questionnaireData)) {
            questions = state.questionnaireData.groups.flatMap(
              (group: any) => group.metadata.fields
            );
          }

          if (questions.length > 0 && state.draftAnswers) {
            await clearAttachmentsForDraftAnswers(
              state.hotelId,
              questions,
              state.draftAnswers
            );
          }
        } catch (err) {
          console.error(
            'Failed to clear attachments for draft answers during switch:',
            err
          );
        }
      }
      contextActions.clearDraftAnswers();
      await performSwitch();
    } catch (err) {
      console.error('Failed to switch questionnaire without saving:', err);
    } finally {
      setIsSwitchingQuestionnaire(false);
    }
  };

  const handleSwitchSaveAndContinue = async () => {
    setIsSavingAndSwitching(true);
    try {
      await contextActions.saveAnswers(false);
      await performSwitch();
    } catch (err) {
      console.error('Failed to save and switch questionnaire:', err);
    } finally {
      setIsSavingAndSwitching(false);
    }
  };
  const handleFastTrackOption = async (optionType: QuestionnaireType) => {
    // Mirror HotelsTable flow: call setQuestionnaireType API first, then reload
    // so the context is re-initialised with the correct type from scratch.
    // Calling fetchSubmissions(isFasttrack) in-place causes a 403 because the
    // backend hasn't committed the type change yet at that point.
    setIsSavingAndSwitching(true);
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.hotels.setQuestionnaireType(hotel.id),
        { questionnaire_type: optionType }
      );
      if (response.status === 200) {
        setShowHotelAdminSwitchModal(false);
        navigate(0); // full page reload — re-mounts providers with new questionnaire_type
      } else {
        error('Failed to set preference. Please try again');
      }
    } catch (err) {
      error('Failed to switch assessment path. Please try again');
    } finally {
      setIsSavingAndSwitching(false);
    }
  };

  return (
    <DialogBody>
      <ContentArea>
        <TabsContainer>
          <TabsList>
            {tabItems
              .filter(
                tab => !(!isSuperAdmin && tab.id === 'hotel-orgs-enterprises')
              )
              .filter(
                tab =>
                  !(tab.id === 'benchmark' && !permissions.canViewBenchmark)
              )
              .map(tab => (
                <Tooltip
                  key={tab.id}
                  content={tab.title}
                  position="top"
                  size="sm"
                  icon={
                    <TabItem
                      key={tab.id}
                      ref={el => {
                        tabRefs.current[tab.id] = el;
                      }}
                      $isActive={activeTab === tab.id}
                      $isDisabled={tab.disabled}
                      onClick={() =>
                        handleTabClick(tab.id as NavigationTab, tab.disabled)
                      }
                      disabled={tab.disabled}
                    >
                      <TabIcon
                        $isActive={activeTab === tab.id}
                        $isDisabled={tab.disabled}
                      >
                        <tab.icon />
                      </TabIcon>
                      {tab.label}
                    </TabItem>
                  }
                />
              ))}
            <TabIndicator
              $left={indicatorStyle.left}
              $width={indicatorStyle.width}
            />
          </TabsList>
          {!isSuperAdmin && activeQuestionnaireType === 'FASTTRACK' && (
            <div
              className="self-end underline cursor-pointer text-primary-800 underline-offset-4"
              onClick={handleDetailedAssessmentClick}
            >
              View Detailed Assessment
            </div>
          )}

          {isHotelAdmin && (
            <div
              className="flex items-center gap-2 text-sm cursor-pointer text-primary-600 hover:text-primary-800"
              onClick={() => setShowHotelAdminSwitchModal(true)}
            >
              <ArrowRightLeft size={18} />
              Switch to{' '}
              {activeQuestionnaireType === 'FASTTRACK'
                ? 'Full Assessment'
                : 'Fundamentals'}
            </div>
          )}

          {permissions.canApproveScore && (
            <Button
              // if the hotel is subscribed and can redubmit then show the confirmation modal first
              onClick={() => {
                if (
                  permissions.canSaveQuestionnaire &&
                  permissions.canApproveScore
                ) {
                  setShowApproveConfirmModal(true);
                } else {
                  handleApproveScore();
                }
              }}
              loading={isApprovingScore}
              disabled={isApprovingScore}
              className="ml-4"
            >
              {isApprovingScore
                ? BUTTON_TEXTS.APPROVING
                : BUTTON_TEXTS.APPROVE_SCORE}
            </Button>
          )}
          {/* {permissions.canSaveQuestionnaire &&
              permissions.canApproveScore && (
                <Tooltip
                content="Under Boost and Essential Subscriptions you can resubmit the questionnaire and improve your score"
                maxWidth={320}
                  icon={
                    <Button
                      variant="success"
                      onClick={()=>{
                        
                      }}
                      loading={isPublishingScore}
                      disabled={isPublishingScore}
                    >
                      Enable Resubmission
                    </Button>
                  }
                />
              )} */}
          {permissions.canPublishScore && (
            <Button
              onClick={handlePublishScore}
              loading={isPublishingScore}
              disabled={isPublishingScore}
            >
              {isPublishingScore
                ? BUTTON_TEXTS.PUBLISHING
                : BUTTON_TEXTS.PUBLISH}
            </Button>
          )}

          {permissions.canReopenSubmission && (
            <Button
              onClick={() => setShowReopenConfirmModal(true)}
              disabled={isReopeningSubmission}
              className="ml-4"
            >
              {BUTTON_TEXTS.REOPEN_SUBMISSION}
            </Button>
          )}

          {/* Auto Publish Timer */}
          {(permissions.canPublishScore || permissions.canApproveScore) &&
            hotel.AutoPublishDate && (
              <AutoPublishTimer autoPublishDate={hotel.AutoPublishDate} />
            )}

          {permissions.canSwitchQuestionnaireType && (
            <div
              className="flex items-center gap-2 text-sm cursor-pointer text-primary-600 hover:text-primary-800"
              onClick={handleSwitchQuestionnaire}
            >
              <ArrowRightLeft size={18} />
              Switch to{' '}
              {activeQuestionnaireType === 'FASTTRACK'
                ? 'Full Questionnaire'
                : 'FastTrack'}
            </div>
          )}
        </TabsContainer>
        <ContentContainer>
          {renderTabContent()}
          {/* Show different buttons based on context */}
          {activeTab === 'submit' && isLastStep() && checkLoading() && (
            <div className="mt-4">
              {/* Approval Status Info Banner for Super Admin */}
              {permissions.canApproveSource && renderApprovalStatusBanner()}
              <div className="flex gap-3 justify-end">
                {/* Release to Hotel button - shown when backend allows */}
                {permissions.canReleaseToHotel && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleSetAsScored}
                    disabled={isSettingAsScored}
                    style={{ minWidth: 160 }}
                  >
                    {isSettingAsScored ? (
                      <span className="flex items-center justify-center gap-2 w-full">
                        <Loader spinnerClassName="!w-4 !h-4 !border-2" />
                        {BUTTON_TEXTS.RELEASING}
                      </span>
                    ) : (
                      <span className="w-full text-center">
                        {BUTTON_TEXTS.RELEASE_TO_HOTEL}
                      </span>
                    )}
                  </Button>
                )}

                {/* Calculate ALO Score button - shown when backend allows */}
                {permissions.canCalculateAloScore && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleCalculateALOScore}
                    disabled={isCalculatingScore}
                    style={{ minWidth: 204 }}
                  >
                    {isCalculatingScore ? (
                      <span className="flex items-center justify-center gap-2 w-full">
                        <Loader spinnerClassName="!w-4 !h-4 !border-2" />
                        {BUTTON_TEXTS.CALCULATING}
                      </span>
                    ) : (
                      <span className="w-full text-center">
                        {BUTTON_TEXTS.CALCULATE_ALO_SCORE}
                      </span>
                    )}
                  </Button>
                )}

                {/* Save Review button - shown when backend allows */}
                {permissions.canSaveReview && (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => saveReview(true)}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? BUTTON_TEXTS.SAVING
                      : BUTTON_TEXTS.SAVE_REVIEW}
                  </Button>
                )}
                {/* Submit and Resubmit buttons - shown when backend allows */}
                <>
                  {stepsData?.totalSteps === stepsData?.completedSteps &&
                    permissions.canSubmitQuestionnaire &&
                    checkLoading() && (
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleSubmitQuestionnaire}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? BUTTON_TEXTS.SUBMITTING
                          : BUTTON_TEXTS.SUBMIT_QUESTIONNAIRE}
                      </Button>
                    )}
                  {permissions.canResubmitQuestionnaire && checkLoading() && (
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleSubmitQuestionnaire}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? BUTTON_TEXTS.RESUBMITTING
                        : BUTTON_TEXTS.RESUBMIT_QUESTIONNAIRE}
                    </Button>
                  )}
                </>
              </div>
            </div>
          )}
        </ContentContainer>
      </ContentArea>

      {/* Sliding Panel for Detailed Assessment */}
      {isDetailedAssessmentOpen && (
        <SlidingPanelOverlay
          $isOpen={isDetailedAssessmentOpen}
          $isClosing={isClosing}
          $isOpening={isOpening}
          onClick={handleCloseDetailedAssessment}
        >
          <SlidingPanelContainer
            $isOpen={isDetailedAssessmentOpen}
            $isClosing={isClosing}
            $isOpening={isOpening}
            onClick={e => e.stopPropagation()}
          >
            <SlidingPanelHeader>
              <div className="flex items-center gap-3">
                <span className="ring-2 ring-green-700  p-1 bg-planet-green rounded-md text-white">
                  <FileIcon />
                </span>
                <SlidingPanelTitle>Full Detailed Assessment</SlidingPanelTitle>
              </div>
              <SlidingPanelCloseButton onClick={handleCloseDetailedAssessment}>
                <X />
              </SlidingPanelCloseButton>
            </SlidingPanelHeader>
            <SlidingPanelBody>
              <FullQuestionnaireReadonly hotelId={state.hotelId || undefined} />
            </SlidingPanelBody>
          </SlidingPanelContainer>
        </SlidingPanelOverlay>
      )}

      {/* Switch Assessment Path Modal for Hotel Admins (IN_PROGRESS) */}
      {showHotelAdminSwitchModal && (
        <SwitchAssessmentPathModal
          currentPath={activeQuestionnaireType}
          isLoading={isSavingAndSwitching}
          onSwitch={handleFastTrackOption}
          onClose={() => setShowHotelAdminSwitchModal(false)}
        />
      )}

      {/* Switch Questionnaire Type Confirmation Modal */}
      <NavigationConfirmModal
        isOpen={showSwitchModal}
        onClose={() => setShowSwitchModal(false)}
        onConfirm={handleSwitchDiscardAndContinue}
        onSave={handleSwitchSaveAndContinue}
        targetStep={
          activeQuestionnaireType === 'FASTTRACK'
            ? 'Full Questionnaire'
            : 'FastTrack Questionnaire'
        }
        isConfirmingNavigation={isSwitchingQuestionnaire}
        isSavingNavigation={isSavingAndSwitching}
      />

      {/* Approve Score Confirmation Modal */}
      <ConfirmationModal
        isOpen={showApproveConfirmModal}
        onClose={() => setShowApproveConfirmModal(false)}
        onConfirm={() => {
          setShowApproveConfirmModal(false);
          handleApproveScore();
        }}
        title="Approve Score"
        headerText="Are you sure you want to approve this score?"
        warningText="Note: Once you approve the score, you will no longer be able to resubmit the questionnaire to improve your score."
        confirmButtonText="Approve Score"
        cancelButtonText="Cancel"
        variant="warning"
        isLoading={isApprovingScore}
      />

      {/* Reopen Submission Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReopenConfirmModal}
        onClose={() => setShowReopenConfirmModal(false)}
        onConfirm={() => {
          // setShowReopenConfirmModal(false);
          handleReopenSubmission();
        }}
        title="Reopen Submission"
        headerText="Are you sure you want to resubmit?"
        confirmButtonText="Resubmit"
        cancelButtonText="Cancel"
        variant="warning"
        isLoading={isReopeningSubmission}
      />
    </DialogBody>
    // </QuestionRefsProvider>
  );
};

export default HotelDetailDialogBody;
